import React, { useState, useRef } from 'react';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { Form, Formik, FormikHelpers } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import {
  useTranslator,
  FormikInput as Input,
  useToast,
  useAppContext,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button, Alert } from 'reactstrap';
import validationSchema from './validationSchema';
import { normalizeDescribeMeal } from '../helpers/normalizers';

interface DescribeMealContainerProps {
  extractIngredients: (input: any) => Promise<any>;
  cancelExtractIngredients: () => void;
  fromUserMeals?: boolean;
}

const DescribeMealContainer: React.FC<DescribeMealContainerProps> = ({
  extractIngredients,
  cancelExtractIngredients,
  fromUserMeals,
}) => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const navigate = useNavigate();
  const isCanceledRef = useRef(false);

  const {
    images: { aiStarDarkIcon, aiStarLightIcon, microphonePrimaryIcon },
  } = useAppContext();

  const [noFoodError, setNoFoodError] = useState(false);
  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);

  const formikValues = {
    description: '',
  };

  const handleSubmit = async (
    values: typeof formikValues,
    { setSubmitting }: FormikHelpers<typeof formikValues>
  ) => {
    setNoFoodError(false);
    setRateLimitExceeded(false);

    // Prevent retry if user canceled the previous request
    if (isCanceledRef.current) {
      isCanceledRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      const response = await extractIngredients({
        mealDescription: values.description,
      });

      if (response.__typename === 'ExtractedIngredients') {
        sessionStorage.setItem('tracker_depth', '2');

        // calculate random id
        const dietMealId = Math.floor(Math.random() * 1000000000);

        const normalizedProps = normalizeDescribeMeal({
          ...response,
          dietMealId,
        });

        if (fromUserMeals) {
          navigate(`/meals/user-meals/describe-meal/meal-details/${dietMealId}`, {
            state: {
              mealData: normalizedProps.mealData,
              requestId: response.requestId,
              timestamp: Date.now(),
            },
          });
        } else {
          navigate(`/tracker/describe-meal/meal-details/${dietMealId}`, {
            state: {
              mealData: normalizedProps.mealData,
              requestId: response.requestId,
              timestamp: Date.now(),
            },
          });
        }
      } else if (response.__typename === 'NotFoodError') {
        setNoFoodError(true);
      } else if (response.__typename === 'RateLimitExceeded') {
        setRateLimitExceeded(true);
      } else {
        toastError();
      }
    } catch (error) {
      // Don't show error if operation was canceled
      // Note: setSubmitting(false) is already called in handleCancel
      if (isCanceledRef.current || (error as Error)?.name === 'AbortError') {
        return;
      }
      // For other errors, reset submitting state and show error
      setSubmitting(false);
      toastError();
    }
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.describeFood.navbar.Title')}</TitleBar>

      <Formik
        onSubmit={handleSubmit}
        initialValues={formikValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
      >
        {({ isValid, values, isSubmitting, setSubmitting }) => {
          const handleCancel = () => {
            // Set flag to prevent Formik from retrying the submit
            isCanceledRef.current = true;
            // Cancel the ongoing GraphQL mutation
            cancelExtractIngredients();
            // Reset submitting state immediately
            setSubmitting(false);
            // Reset flag to allow retry
            setTimeout(() => {
              isCanceledRef.current = false;
            }, 0);
          };

          return (
            <Form>
              <div className="page-template-tracker edit-meal-page">
                <Container className="py-md-4 full-height4">
                  <Row>
                    <Col md={{ size: 10, offset: 1 }}>
                      <div className="page-container">
                        <div className="tracker-container">
                          <h2 className="title-medium semi-bold">
                            {t('tracker.describeFood.title')}
                          </h2>
                          <p className="small text-text-variant-color">
                            {t('tracker.describeFood.description')}
                          </p>
                          <Input
                            name="description"
                            type="textarea"
                            placeholder={t('tracker.describeFood.input.placeholder')}
                            required
                            translateErrMsg
                          />

                          <div className="d-flex gap-2">
                            <p className="small medium">
                              {t('tracker.describeFood.example.title')}
                            </p>
                            <p className="small text-text-variant-color">
                              {t('tracker.describeFood.example.content')}
                            </p>
                          </div>

                          {noFoodError && (
                            <Alert color="warning" className="mt-xl mb-0" fade={false}>
                              <p className="mb-0">
                                {t('tracker.describeFood.analyze.noFood.notification')}
                              </p>
                            </Alert>
                          )}

                          {rateLimitExceeded && (
                            <Alert color="danger" className="mt-xl mb-0" fade={false}>
                              <p className="mb-0">{t('tracker.limitUsageError.notification')}</p>
                            </Alert>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>

                <div className="sticky-footer">
                  <Container className="px-3">
                    {!isSubmitting ? (
                      <Row>
                        <Col xs="12" md={{ size: 4, offset: 4 }}>
                          <Button
                            type="button"
                            tag={Link}
                            to={
                              fromUserMeals
                                ? '/meals/user-meals/describe-meal/by-voice'
                                : '/tracker/describe-meal/by-voice'
                            }
                            color="secondary"
                            outline
                            className="w-100 mb-s"
                            replace
                          >
                            <img
                              src={microphonePrimaryIcon}
                              alt=""
                              style={{ marginTop: '-3px', marginBottom: '-3px' }}
                              className="me-2"
                            />
                            {t('tracker.describeFood.button.byVoice')}
                          </Button>

                          <Button
                            type="submit"
                            color="primary"
                            disabled={!isValid}
                            className="w-100"
                          >
                            <img
                              src={aiStarLightIcon}
                              alt=""
                              style={{ marginTop: '-3px', marginBottom: '-3px' }}
                              className="me-2"
                            />
                            {t('tracker.describeFood.button.analyze')}
                          </Button>
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col xs="12" className="text-center">
                          <p className="description text-text-variant-color2">
                            {t('tracker.describeFood.loader.analyzing')}
                          </p>
                        </Col>
                        <Col xs="4">
                          <Button type="button" color="link" onClick={handleCancel}>
                            {t('tracker.describeFood.loader.cancelButton')}
                          </Button>
                        </Col>
                        <Col xs="4">
                          <div className="ai-star">
                            <img src={aiStarDarkIcon} alt="" />
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Container>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default DescribeMealContainer;
